import * as React from 'react'
import * as ReactDOM from 'react-dom'
import classnames from 'classnames'
import {Popover as PopoverBase} from './base'

export type PopoverTriggerType = 'click' | 'hover'

interface PopoverProps {
  /**
   * attachTop - Optional boolean, indicating whether or not
   *  the popover should be displayed above or below the
   *  triggering element. Defaults to false.
   */
  attachTop?: boolean
  /**
   * popoverClass - Optional string representing classnames
   *   to be applied to the popover element. Defaults to
   *  'popover'.
   */
  popoverClass?: string
  /**
   * noArrow - Optional boolean, indicating whether or not
   *  the popover should have an arrow. Defaults to false.
   */
  noArrow?: boolean
  /**
   * triggerType - Optional string indicating how the popover
   *   should be triggered. Available options are 'click' and
   *   'hover'. A 'click' popover will be cliggered when the
   *   user clicks on the trigger, and dismissed when they click
   *   elsewhere (or press escape). A 'hover' popover will be
   *   triggered when the user moves their mouse over the triggering
   *   element, and dismissed when the user moves the mouse away.
   *   Defaults to 'click'.
   */
  triggerType?: PopoverTriggerType
  /**
   * additionalOffsetTop - Optional number allowing the consumer
   *   of the component to manually effect the vertical offset
   *   of the popover element. A negative number will move the
   *   popover towards the top of the viewport, and a positive
   *   will move it towards the bottom. Defaults to 0.
   */
  additionalOffsetTop?: number
  /**
   * trigger - The react node which the user can interact
   *  with to trigger the popover.
   */
  trigger: React.ReactNode
  /**
   * children - The react node which will be rendered when
   *   the popover is triggered.
   */
  children: React.ReactNode
}

type SpanProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLSpanElement>,
  HTMLSpanElement
>

/**
 * @deprecated Use components/Popover instead
 */
export default class Popover extends React.Component<PopoverProps> {
  private static defaultProps = {
    attachTop: false,
    noArrow: false,
    additionalOffsetTop: 0,
  }

  public readonly popover: PopoverBase

  constructor(props: PopoverProps) {
    super(props)
    this.popover = new PopoverBase({
      attachTop: this.props.attachTop,
      className: this.props.popoverClass,
      additionalOffsetTop: this.props.additionalOffsetTop,
    })
  }

  public open: React.ReactEventHandler = (ev) => {
    ev.stopPropagation()
    if (!this.popover.is_open) {
      ev.preventDefault()
      this.popover.open(ev.currentTarget)
    }
  }

  public close: React.ReactEventHandler = (ev) => {
    ev.stopPropagation()
    if (this.popover.is_open) {
      ev.preventDefault()
      this.popover.close()
    }
  }

  public componentWillUnmount(): void {
    this.popover.end()
  }

  public render(): React.ReactNode {
    return (
      <React.Fragment>
        <span {...this.getTriggerWrapperProps()}>{this.props.trigger}</span>
        <Portal target={this.popover.node}>
          <Wrapper
            attachTop={this.props.attachTop}
            noArrow={this.props.noArrow}
          >
            {this.props.children}
          </Wrapper>
        </Portal>
      </React.Fragment>
    )
  }

  private getTriggerWrapperProps(): SpanProps {
    const {triggerType = 'click'} = this.props
    switch (triggerType) {
      case 'click':
        return {onClick: this.open.bind(this)}
      case 'hover':
        return {
          onMouseEnter: this.open.bind(this),
          onMouseLeave: this.close.bind(this),
        }
    }
  }
}

const Wrapper: React.FunctionComponent<{
  attachTop?: boolean
  noArrow?: boolean
}> = (props) => {
  const direction = props.attachTop ? 'down' : 'up'
  const arrow = !props.noArrow && (
    <span className={classnames('arrow', direction)} />
  )

  return (
    <React.Fragment>
      {props.attachTop || arrow}
      <div className="popover-content">{props.children}</div>
      {props.attachTop && arrow}
    </React.Fragment>
  )
}

const Portal: React.FunctionComponent<{target: HTMLElement}> = (props) =>
  ReactDOM.createPortal(props.children, props.target)
